import { useLocation, useNavigate } from "react-router-dom";
import { PersonResult, Result } from "../types/interfaces";
import PageHeader from "../components/PageHeader";
import { TYPE_LABELS } from "../utils/titleType";

export default function TitlesPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const movieOrShowResponseResults: Result[] = location.state?.results || []; // Get passed data, default to empty array if missing
    // People matching the same query. A search for an actor's name returns
    // these alongside (or instead of) titles.
    const peopleResults: PersonResult[] = location.state?.people || [];
    // Set when the back-end corrected a misspelled search against its title index.
    const didYouMean: string | undefined = location.state?.didYouMean;
    const correctedFrom: string | undefined = location.state?.correctedFrom;

    const handleMovieShowCardClick = (item: Result) => {
        navigate(`/movieShowDetailsPage/${item.id}`);
    };

    const resultCount = movieOrShowResponseResults.length;
    const peopleCount = peopleResults.length;

    return (
        <div className="page">
            <PageHeader />

            <div className="page-inner">
                {didYouMean && (
                    <p className="search-correction">
                        No results for <b>{correctedFrom}</b> — showing <b>{didYouMean}</b> instead
                    </p>
                )}

                {/* People come first. Searching a person's name is a deliberate
                    act, and the titles below are usually documentaries *about*
                    them rather than anything they were in. */}
                {peopleCount > 0 && (
                    <section className="people-section">
                        <h2 className="section-heading">
                            {peopleCount === 1 ? "Person" : "People"}
                        </h2>
                        <div className="people-card-container">
                            {peopleResults.map((person) => (
                                <button
                                    key={person.id}
                                    onClick={() => navigate(`/personPage/${person.id}`)}
                                    className="person-card"
                                >
                                    <div className="person-card-photo">
                                        {person.image_url ? (
                                            <img src={person.image_url} alt="" loading="lazy" />
                                        ) : (
                                            <div className="person-card-photo-empty" aria-hidden="true">
                                                👤
                                            </div>
                                        )}
                                    </div>
                                    <div className="person-card-body">
                                        <span className="person-card-name">{person.name}</span>
                                        {person.type ? (
                                            <span className="badge">{person.type}</span>
                                        ) : null}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </section>
                )}

                {peopleCount > 0 && resultCount > 0 && (
                    <h2 className="section-heading">Titles</h2>
                )}

                {resultCount > 0 && (
                    <p className="results-count">
                        {resultCount} {resultCount === 1 ? "result" : "results"}
                    </p>
                )}

                {/* An actor search often matches no title at all, and an empty
                    grid under a populated People section reads as a failure. */}
                {(resultCount > 0 || peopleCount === 0) && (
                    <div className="titles-card-container">
                        {resultCount > 0 ? (
                            movieOrShowResponseResults.map((item: Result) => (
                                <button
                                    key={item.id}
                                    onClick={() => handleMovieShowCardClick(item)}
                                    className="title-card"
                                >
                                    <div className="title-card-poster">
                                        {item.image_url ? (
                                            <img
                                                src={item.image_url}
                                                alt=""
                                                loading="lazy"
                                            />
                                        ) : (
                                            // Roughly one result in five has no image, which
                                            // used to render as a broken-image icon.
                                            <div className="title-card-poster-empty" aria-hidden="true">
                                                🎬
                                            </div>
                                        )}
                                    </div>
                                    <div className="title-card-body">
                                        <span className="title-card-name">{item.name}</span>
                                        <span className="title-card-meta">
                                            {item.year ? <span>{item.year}</span> : null}
                                            <span className="badge">
                                                {TYPE_LABELS[item.type] || item.type}
                                            </span>
                                        </span>
                                    </div>
                                </button>
                            ))
                        ) : (
                            <p className="empty-state">
                                Nothing to show. <a href="/">Start a new search</a>.
                            </p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
